import { useEffect, useState } from "react";

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DnsIcon from "@mui/icons-material/Dns";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import axios from "axios";

import { InfraTargetEndpoints } from "@generated/infrastructure-targets";
import { FEATURE_CARD_ACCENT_WIDTH } from "@shell/styles/designTokens";
import { orgSessionManager } from "src/domains/auth/services/orgSessionManager";
import { apiBaseUrls } from "src/platform/constants/shared-constants";

import { InfraType, InfrastructureTarget } from "../../types";

interface Props {
  organizationAlias: string;
  infraType: InfraType;
  value: InfrastructureTarget | null;
  /** Called with null to explicitly skip target selection */
  onChange: (target: InfrastructureTarget | null) => void;
  /** Auto-select the target with this ID after loading (restored from URL state) */
  preselectedId?: number;
}

const STATUS_COLOR: Record<
  string,
  "success" | "warning" | "error" | "default"
> = {
  active: "success",
  pending: "warning",
  failed: "error",
  disconnected: "default",
};

export function StepChooseInfraTarget({
  organizationAlias,
  infraType,
  value,
  onChange,
  preselectedId,
}: Props) {
  const [targets, setTargets] = useState<InfrastructureTarget[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Rusha Hosted — no target needed, Rusha manages infra
  if (infraType === "rusha_hosted") {
    return (
      <Box>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          Infrastructure target
        </Typography>
        <Alert severity="success" icon={<RocketLaunchIcon />} sx={{ mt: 1 }}>
          <Typography fontWeight={600}>
            Rusha manages infrastructure for you
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            No target registration required. Rusha will provision and manage the
            underlying infrastructure automatically. Click <strong>Next</strong>{" "}
            to continue.
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 1 }}
          >
            Container Registry: ghcr.io/rusha-corp · Artifact Registry:
            minio.rusha.dev
          </Typography>
        </Alert>
      </Box>
    );
  }

  // All other infra types — fetch from API
  return (
    <FetchedTargetList
      organizationAlias={organizationAlias}
      infraType={infraType}
      value={value}
      onChange={onChange}
      preselectedId={preselectedId}
      loading={loading}
      setLoading={setLoading}
      error={error}
      setError={setError}
      targets={targets}
      setTargets={setTargets}
    />
  );
}

// ── Split out so hooks run unconditionally ─────────────────────────────────────

interface FetchedProps extends Props {
  loading: boolean;
  setLoading: (v: boolean) => void;
  error: string | null;
  setError: (v: string | null) => void;
  targets: InfrastructureTarget[];
  setTargets: (v: InfrastructureTarget[]) => void;
}

function FetchedTargetList({
  organizationAlias,
  infraType,
  value,
  onChange,
  preselectedId,
  loading,
  setLoading,
  error,
  setError,
  targets,
  setTargets,
}: FetchedProps) {
  const theme = useTheme();
  // Auto-select preselected target after targets load (restores URL state on refresh)
  useEffect(() => {
    if (!value && preselectedId && targets.length > 0) {
      const match = targets.find((t) => t.id === preselectedId);
      if (match) onChange(match);
    }
  }, [targets, preselectedId, value, onChange]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    const fetchTargets = async () => {
      try {
        const res = await axios.get<InfrastructureTarget[]>(
          `${apiBaseUrls.BACKEND}${InfraTargetEndpoints.LIST_TARGETS}`,
          {
            params: { organizationAlias },
            headers: {
              Authorization: `Bearer ${orgSessionManager.getToken() ?? ""}`,
            },
          },
        );
        if (!cancelled) {
          const filtered = res.data.filter((t) => t.infraType === infraType);
          setTargets(filtered);
        }
      } catch (e) {
        if (!cancelled) {
          setError(
            e instanceof Error
              ? e.message
              : "Failed to load infrastructure targets",
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void fetchTargets();
    return () => {
      cancelled = true;
    };
  }, [organizationAlias, infraType, setLoading, setError, setTargets]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        Choose infrastructure target
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Select a registered cluster or server to deploy to. Only targets
        matching the selected infrastructure type are shown.
      </Typography>

      {targets.length === 0 ? (
        <Box>
          <Alert severity="info" sx={{ mb: 2 }}>
            No infrastructure targets registered for this infra type. Register
            one first in <strong>Infrastructure Targets</strong>, or continue
            without a target and assign one later.
          </Alert>
          <Button
            variant="outlined"
            onClick={() => onChange(null)}
            sx={{ mt: 1 }}
          >
            Continue without target
          </Button>
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {targets.map((target) => {
            const isSelected = value?.id === target.id;
            return (
              <Paper
                key={target.id}
                variant="outlined"
                role="button"
                tabIndex={0}
                aria-label={`Select ${target.name}`}
                onClick={() => onChange(target)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onChange(target);
                  }
                }}
                sx={{
                  p: 2,
                  cursor: "pointer",
                  borderRadius: 1,
                  border: `1px solid ${isSelected ? theme.palette.primary.main : theme.palette.divider}`,
                  borderTopWidth: isSelected ? FEATURE_CARD_ACCENT_WIDTH : 1,
                  borderTopColor: isSelected
                    ? theme.palette.primary.main
                    : theme.palette.divider,
                  bgcolor: isSelected
                    ? alpha(theme.palette.primary.main, 0.03)
                    : "background.paper",
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  transition: "border-color 0.15s, background-color 0.15s",
                  "&:hover": { borderColor: theme.palette.primary.main },
                  "&:focus-visible": {
                    outline: "2px solid",
                    outlineColor: theme.palette.primary.main,
                    outlineOffset: 2,
                  },
                }}
              >
                <DnsIcon color={isSelected ? "primary" : "action"} />
                <Box sx={{ flex: 1 }}>
                  <Typography fontWeight={600}>{target.name}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {target.region}
                    {target.clusterName ? ` · ${target.clusterName}` : ""}
                    {target.hostname ? ` · ${target.hostname}` : ""}
                  </Typography>
                  {target.containerRegistry && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mt: 0.25 }}
                    >
                      {target.containerRegistryType?.toUpperCase() ??
                        "Registry"}
                      : {target.containerRegistry}
                      {target.artifactRegistry
                        ? ` · Artifacts: ${target.artifactRegistry}`
                        : ""}
                    </Typography>
                  )}
                </Box>
                <Chip
                  label={target.status}
                  size="small"
                  color={STATUS_COLOR[target.status] ?? "default"}
                />
                {isSelected && <CheckCircleIcon color="primary" />}
              </Paper>
            );
          })}
        </Box>
      )}
    </Box>
  );
}
